/**
 * サイト内行動の dataLayer プッシュ（GA4 / GTM 用）
 *
 * テーマ側のイベント名にはすべて fs_ を付ける（GTM 側で GA4 のイベント名に読み替える）。
 * 商品一覧（view_item_list / select_item）は component-list-tracking.js、
 * カート系（fs_add_to_cart 等）は component-datalayer-cart.js が担当。
 *
 * [プロモーション]
 *   fs_view_promotion / fs_select_promotion : [data-promotion] コンテナ（data-promotion-id / data-promotion-name）。
 *     スライドは [data-promotion-slide]（data-creative-name / data-creative-slot）。表示は IntersectionObserver で 1 回だけ
 * [商品詳細]
 *   fs_variant_select, fs_size_chart_open, fs_product_tab_open, fs_product_media_view, fs_share_click,
 *   fs_store_availability_open, fs_back_in_stock_open / fs_back_in_stock_request,
 *   fs_add_to_wishlist / fs_remove_from_wishlist（Wishlist King のボタン）
 * [検索]
 *   fs_search（検索語は送らない。文字数のみ）, fs_view_search_results, fs_predictive_search_open / fs_predictive_search_click,
 *   fs_search_keyword_click
 * [回遊]
 *   fs_menu_click, fs_gender_switch, fs_collection_filter, fs_collection_sort, fs_layout_switch, fs_pagination_click,
 *   fs_breadcrumb_click, fs_shop_list_click, fs_social_click
 * [会員・リード]
 *   fs_login / fs_sign_up（送信）, fs_login_success / fs_sign_up_success（次ページでログイン済みなら）,
 *   fs_newsletter_signup, fs_popup_view / fs_popup_close / fs_popup_click
 */
(() => {
  window.dataLayer = window.dataLayer || [];
  const page = window.fsPageData || {};

  const push = (event, params = {}) => {
    window.dataLayer.push({ event, ...params });
  };
  const text = (el) => (el ? (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 100) : '');
  const href = (a) => {
    try {
      return new URL(a.getAttribute('href'), location.href).href;
    } catch (_) {
      return a.getAttribute('href') || '';
    }
  };
  const isVisible = (el) => el.getClientRects().length > 0;
  const on = (type, selector, handler, opts = true) => {
    document.addEventListener(
      type,
      (e) => {
        const target = e.target instanceof Element ? e.target : null;
        const el = target && target.closest(selector);
        if (el) handler(el, e, target);
      },
      opts
    );
  };
  const onceSet = new WeakSet();

  // 商品情報（商品ページなら fsPageData、それ以外はカードの data 属性から）
  const productParams = (el) => {
    const card = el instanceof Element ? el.closest('[data-product-id]') : null;
    if (card && card.dataset.itemId) {
      return { item_id: card.dataset.itemId, item_name: card.dataset.itemName, item_variant: card.dataset.itemVariant, price: Number(card.dataset.price) };
    }
    if (page.product_id) return { item_id: String(page.product_id), item_category: page.product_type, item_brand: page.product_vendor };
    return card ? { item_id: card.dataset.productId } : {};
  };

  // ---- プロモーション --------------------------------------------------------
  const promoParams = (container, slide) => {
    const slides = slide ? [...container.querySelectorAll('[data-promotion-slide]')] : [];
    return {
      promotion_id: container.dataset.promotionId,
      promotion_name: container.dataset.promotionName,
      creative_name: slide ? slide.dataset.creativeName || text(slide) : container.dataset.creativeName || '',
      creative_slot: slide ? slide.dataset.creativeSlot || String(slides.indexOf(slide) + 1) : '',
    };
  };
  const promoPush = (event, container, slide, extra = {}) => push(event, { ...promoParams(container, slide), ...extra });

  const promoObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting || onceSet.has(entry.target)) return;
              onceSet.add(entry.target);
              promoObserver.unobserve(entry.target);
              const container = entry.target.closest('[data-promotion]');
              if (!container) return;
              promoPush('fs_view_promotion', container, entry.target.hasAttribute('data-promotion-slide') ? entry.target : null);
            });
          },
          { threshold: 0.5 }
        )
      : null;
  const observePromotions = () => {
    if (!promoObserver) return;
    document.querySelectorAll('[data-promotion]').forEach((container) => {
      const slides = container.querySelectorAll('[data-promotion-slide]');
      (slides.length ? [...slides] : [container]).forEach((el) => {
        if (!onceSet.has(el)) promoObserver.observe(el);
      });
    });
  };
  on('click', '[data-promotion] a[href]', (a) => {
    const container = a.closest('[data-promotion]');
    promoPush('fs_select_promotion', container, a.closest('[data-promotion-slide]'), { link_url: href(a), link_text: text(a) });
  });

  // ---- 商品詳細 --------------------------------------------------------------
  document.addEventListener('variant:changed', (e) => {
    const v = e.detail && e.detail.variant;
    if (!v) return;
    push('fs_variant_select', {
      ...productParams(e.target),
      item_variant: String(v.id),
      variant_title: v.title,
      variant_available: !!v.available,
      price: typeof v.price === 'number' ? v.price / 100 : undefined,
    });
  });
  on('click', '.ProductForm__SizeChartButton', (el) => push('fs_size_chart_open', productParams(el)));
  on('click', '[data-section-type="product"] [data-action="toggle-collapsible"]', (el) => {
    if (el.getAttribute('aria-expanded') === 'true') return; // 閉じる操作は送らない
    push('fs_product_tab_open', { ...productParams(el), tab_name: text(el) });
  });
  ['video:played', 'model:played'].forEach((name) => {
    document.addEventListener(name, (e) => {
      if (onceSet.has(e.target)) return;
      onceSet.add(e.target);
      push('fs_product_media_view', { ...productParams(e.target), media_type: name.split(':')[0] });
    });
  });
  on('click', '.Product__ShareItem', (a) => push('fs_share_click', { ...productParams(a), method: text(a) || href(a) }));
  on('click', '.store-availability-information__link', (el) => push('fs_store_availability_open', productParams(el)));
  // Back in Stock アプリ（再入荷通知）
  on('click', '#BIS_trigger, .bis_trigger, [data-bis-trigger], a[href="#BIS_trigger"]', (el) => push('fs_back_in_stock_open', productParams(el)));
  on('submit', '#BIS_form_form, form[id^="BIS"], form.bis-form', (form) => push('fs_back_in_stock_request', productParams(form)));
  // Wishlist King
  on('click', '[data-wk-add-product], [data-wk-add-to-cart]', (el) => {
    if (el.hasAttribute('data-wk-add-to-cart')) return;
    push('fs_add_to_wishlist', productParams(el));
  });
  on('click', '[data-wk-remove-product], [data-wk-remove-item]', (el) => push('fs_remove_from_wishlist', productParams(el)));

  // ---- 検索 ------------------------------------------------------------------
  on('submit', 'form[role="search"], form[action*="/search"]', (form) => {
    const input = form.querySelector('[name="q"]');
    push('fs_search', { search_term_length: input ? input.value.trim().length : 0, search_location: form.closest('#Search') ? 'header' : 'page' });
  });
  if (page.page_type === 'search') {
    push('fs_view_search_results', {
      search_performed: !!page.search_performed,
      search_results_count: page.search_results_count || 0,
      search_zero_results: !!page.search_performed && !page.search_results_count,
      search_term_length: page.search_term_length || 0,
    });
  }
  const searchEl = document.getElementById('Search');
  if (searchEl) {
    new MutationObserver(() => {
      if (searchEl.getAttribute('aria-hidden') === 'false') push('fs_predictive_search_open');
    }).observe(searchEl, { attributes: true, attributeFilter: ['aria-hidden'] });
  }
  on('click', '#Search .Search__Results a[href], #Search .Search__Results [data-list-item]', (el) => {
    const a = el.matches('a') ? el : el.querySelector('a[href]');
    push('fs_predictive_search_click', {
      ...productParams(el),
      link_url: a ? href(a) : '',
      link_text: text(el),
      result_type: el.closest('[data-list-item]') ? 'product' : el.closest('.Segment__Title') ? 'view_all' : 'other',
    });
  });
  on('click', '[data-search-keywords] a, [data-search-keywords] button', (el) => push('fs_search_keyword_click', { keyword: text(el) }));

  // ---- 回遊 ------------------------------------------------------------------
  const MENUS = [
    ['.Header__MainNav', 'header'],
    ['.MegaMenu', 'mega_menu'],
    ['.SidebarMenu', 'sidebar'],
    ['.BottomNav', 'bottom_nav'],
    ['.Footer', 'footer'],
    ['.Search__MenuNav', 'search_menu'],
    ['.Header__Icon, .Header__Wrapper', 'header_icon'],
  ];
  on('click', 'a[href]', (a) => {
    if (a.closest('[data-item-list], [data-promotion], .Product__ShareList, .breadcrumb, .Pagination')) return;
    const hit = MENUS.find(([sel]) => a.closest(sel));
    if (!hit) return;
    push('fs_menu_click', { menu_location: hit[1], link_text: text(a) || a.getAttribute('aria-label') || '', link_url: href(a) });
  });
  on('click', '.GenderSwitch__Item', (el) => push('fs_gender_switch', { gender: el.classList.contains('GenderSwitch__Item--men') ? 'men' : 'women' }));
  on('change', '.collection-filters-form input', (input) => {
    if (input.type === 'hidden') return;
    push('fs_collection_filter', {
      filter_name: input.name,
      filter_value: input.value,
      filter_checked: input.type === 'checkbox' ? input.checked : true,
      list_page_type: page.page_type,
    });
  });
  on('click', '#collection-sort-popover [data-action="select-value"]', (el) => push('fs_collection_sort', { sort_value: el.dataset.value }));
  on('click', '[data-action="change-layout-mode"]', (el) => push('fs_layout_switch', { grid_type: el.dataset.gridType, grid_count: Number(el.dataset.count) }));
  on('click', '.Pagination__NavItem[href]', (a) => {
    let pageNo = null;
    try {
      pageNo = Number(new URL(href(a)).searchParams.get('page') || 1);
    } catch (_) {}
    push('fs_pagination_click', { page_to: pageNo, direction: a.getAttribute('rel') || 'number' });
  });
  on('click', '.breadcrumb__link', (a) => push('fs_breadcrumb_click', { link_text: text(a), link_url: href(a) }));
  on('click', 'a.map_button, a[href^="tel:"], a[href*="google.com/maps"], a[href*="goo.gl/maps"]', (a) =>
    push('fs_shop_list_click', { link_type: a.getAttribute('href').startsWith('tel:') ? 'tel' : 'map', link_text: text(a), link_url: a.getAttribute('href') })
  );
  const SOCIAL = [
    ['instagram.com', 'instagram'],
    ['facebook.com', 'facebook'],
    ['twitter.com', 'x'],
    ['x.com', 'x'],
    ['line.me', 'line'],
    ['lin.ee', 'line'],
    ['youtube.com', 'youtube'],
    ['tiktok.com', 'tiktok'],
    ['pinterest.', 'pinterest'],
  ];
  on('click', 'a[href]', (a) => {
    if (a.closest('.Product__ShareList')) return;
    const h = href(a);
    const hit = SOCIAL.find(([d]) => h.includes(d));
    if (!hit) return;
    push('fs_social_click', { network: hit[1], link_url: h, link_location: a.closest('.Footer') ? 'footer' : a.closest('[data-section-type]') ? a.closest('[data-section-type]').dataset.sectionType : 'other' });
  });

  // ---- 会員・リード ----------------------------------------------------------
  const AUTH_KEY = 'fs_auth_attempt';
  on('submit', '#customer_login, form[action*="/account/login"]', () => {
    push('fs_login', { method: 'email' });
    try {
      sessionStorage.setItem(AUTH_KEY, 'login');
    } catch (_) {}
  });
  on('submit', '#create_customer, form[action*="/account"][id*="create"]', () => {
    push('fs_sign_up', { method: 'email' });
    try {
      sessionStorage.setItem(AUTH_KEY, 'sign_up');
    } catch (_) {}
  });
  try {
    const attempt = sessionStorage.getItem(AUTH_KEY);
    if (attempt) {
      if (page.customer_logged_in) {
        push(attempt === 'sign_up' ? 'fs_sign_up_success' : 'fs_login_success', { method: 'email' });
        sessionStorage.removeItem(AUTH_KEY);
      } else if (!/\/account\/(login|register)/.test(location.pathname)) {
        sessionStorage.removeItem(AUTH_KEY); // ログインページ以外に移動して未ログインなら失敗扱いで破棄
      }
    }
  } catch (_) {}

  const newsletterLocation = (form) =>
    form.closest('.NewsletterPopup') ? 'popup' : form.closest('.Footer') ? 'footer' : form.closest('.MailMagazine') ? 'mail_magazine_section' : 'section';
  on('submit', 'form', (form) => {
    const tag = form.querySelector('input[name="contact[tags]"]');
    if (!tag || !/newsletter/i.test(tag.value)) return;
    push('fs_newsletter_signup', { method: newsletterLocation(form) });
  });

  // ポップアップ：表示されたら 1 回だけ view、閉じる・クリックはそのたびに
  const POPUPS = '.NewsletterPopup, .Popup, .upsell-popup, .MailMagazine, .SizeChart';
  const popupName = (el) => el.dataset.popupName || el.className.split(/\s+/)[0];
  const checkPopups = () => {
    document.querySelectorAll(POPUPS).forEach((el) => {
      const shown = isVisible(el) && el.getAttribute('aria-hidden') !== 'true' && !(el.tagName === 'DIALOG' && !el.open);
      if (shown && !onceSet.has(el)) {
        onceSet.add(el);
        push('fs_popup_view', { popup_name: popupName(el) });
      }
    });
  };
  on('click', `${POPUPS.split(', ').map((s) => `${s} [data-action="close-popup"], ${s} .Popup__CloseButton, ${s} [data-action="close-size-chart"], ${s} .Drawer__Close, ${s} .SizeChart__Close`).join(', ')}`, (el) =>
    push('fs_popup_close', { popup_name: popupName(el.closest(POPUPS)) })
  );
  on('click', `${POPUPS.split(', ').map((s) => `${s} a[href]`).join(', ')}`, (a) => {
    if (a.closest('[data-promotion], [data-item-list]')) return;
    push('fs_popup_click', { popup_name: popupName(a.closest(POPUPS)), link_url: href(a), link_text: text(a) });
  });

  // ---- DOM 変化の監視（AJAX 差し替え・ポップアップ表示・遅延描画）----------------
  let timer;
  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      observePromotions();
      checkPopups();
    }, 300);
  };
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden', 'class', 'open', 'style'] });
  schedule();
})();
